import * as React from "react";
import { Input } from "@repo/ui";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { cn } from "@repo/utils";

export interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: ParsedAddress) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

interface Prediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as
  | string
  | undefined;

function parseAddressComponents(
  components: google.maps.GeocoderAddressComponent[]
): ParsedAddress {
  let streetNumber = "";
  let route = "";
  let locality = "";
  let sublocality = "";
  let state = "";
  let zip = "";

  for (const component of components) {
    const type = component.types[0];
    switch (type) {
      case "street_number":
        streetNumber = component.long_name;
        break;
      case "route":
        route = component.long_name;
        break;
      case "locality":
        locality = component.long_name;
        break;
      case "sublocality_level_1":
        sublocality = component.long_name;
        break;
      case "administrative_area_level_1":
        state = component.short_name;
        break;
      case "postal_code":
        zip = component.long_name;
        break;
    }
  }

  // Prefer sublocality (e.g. "Staten Island") over locality (e.g. "New York")
  const city = sublocality || locality;
  const street = streetNumber ? `${streetNumber} ${route}` : route;

  return { street, city, state, zip };
}

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = "123 Main Street",
  disabled = false,
  id,
  className,
}: AddressAutocompleteProps) {
  const [predictions, setPredictions] = React.useState<Prediction[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [isLoaded, setIsLoaded] = React.useState(false);

  const autocompleteServiceRef =
    React.useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef =
    React.useRef<google.maps.places.PlacesService | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Load Google Maps SDK on first focus
  const loadSdk = React.useCallback(async () => {
    if (isLoaded || !API_KEY) return;

    try {
      setOptions({ key: API_KEY, v: "weekly" });
      await importLibrary("places");
      autocompleteServiceRef.current =
        new google.maps.places.AutocompleteService();
      // PlacesService requires a DOM node (doesn't render anything)
      const node = document.createElement("div");
      placesServiceRef.current = new google.maps.places.PlacesService(node);
      setIsLoaded(true);
    } catch (err) {
      console.error(
        "[AddressAutocomplete] Failed to load Google Maps SDK:",
        err
      );
    }
  }, [isLoaded]);

  // Fetch predictions with debounce
  const fetchPredictions = React.useCallback((input: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!input.trim() || !autocompleteServiceRef.current) {
      setPredictions([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      autocompleteServiceRef.current!.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: "us" },
          types: ["address"],
        },
        (
          results: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            setPredictions(
              results.map((r) => ({
                placeId: r.place_id,
                description: r.description,
                mainText: r.structured_formatting.main_text,
                secondaryText: r.structured_formatting.secondary_text,
              }))
            );
            setIsOpen(true);
            setActiveIndex(-1);
          } else {
            setPredictions([]);
            setIsOpen(false);
          }
        }
      );
    }, 300);
  }, []);

  // Select a prediction
  const selectPrediction = React.useCallback(
    (prediction: Prediction) => {
      if (!placesServiceRef.current) return;

      placesServiceRef.current.getDetails(
        {
          placeId: prediction.placeId,
          fields: ["address_components"],
        },
        (
          place: google.maps.places.PlaceResult | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place?.address_components
          ) {
            const parsed = parseAddressComponents(place.address_components);
            onChange(parsed.street);
            onAddressSelect(parsed);
          }
        }
      );

      setIsOpen(false);
      setPredictions([]);
    },
    [onChange, onAddressSelect]
  );

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : predictions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < predictions.length) {
          selectPrediction(predictions[activeIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // No API key — render plain input
  if (!API_KEY) {
    return (
      <Input
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={className}
      />
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          fetchPredictions(e.target.value);
        }}
        onFocus={loadSdk}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoComplete="off"
        className={className}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={id ? `${id}-listbox` : undefined}
        aria-activedescendant={
          activeIndex >= 0 ? `prediction-${activeIndex}` : undefined
        }
      />

      {isOpen && predictions.length > 0 && (
        <ul
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          className="absolute z-50 mt-1 w-full rounded-md border border-border bg-surface shadow-md overflow-hidden"
        >
          {predictions.map((prediction, index) => (
            <li
              key={prediction.placeId}
              id={`prediction-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={cn(
                "cursor-pointer px-3 py-2 text-sm transition-colors",
                index === activeIndex
                  ? "bg-primary-soft text-foreground"
                  : "text-foreground hover:bg-primary-soft/50"
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                selectPrediction(prediction);
              }}
            >
              <span className="font-medium">{prediction.mainText}</span>
              <span className="text-muted-foreground ml-1 text-xs">
                {prediction.secondaryText}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
